import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
const MiniCartContainer = lazy(() => import(/* webpackPrefetch: true */ './Containers/MiniCart.container'))
const QuickSearchContainer = lazy(() => import(/* webpackPrefetch: true */ './Containers/QuickSearch.container'))
const NavigationContainer = lazy(() => import(/* webpackPrefetch: true */ './Containers/Navigation.container'))
import DynamicComponent from './Components/DynamicComponent';
import './Containers/Vanilla.container';
const SearchWindow = lazy(() => import(/* webpackPrefetch: true */ './Components/SearchWindow'))
import { ToastContainer } from "react-toastify"
import(/* webpackPrefetch: true */ 'react-toastify/dist/ReactToastify.css');
import AOS from 'aos';
import(/* webpackPrefetch: true */ 'aos/dist/aos.css');
import CookieConsent from './Components/CookieConsent';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
// use the parent page's store if possible, so the iframe will share the same store with the parent context.
// this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
// when reorder action happens.
const store = window.__litium.store = window.__litium.store || (window.parent.__litium && window.parent.__litium.store)
    || createStore(app, preloadState, composeWithDevTools(applyMiddleware(thunk, historyMiddleware)));
window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(<Suspense fallback={""}>{element}</Suspense>, container, callback);
}

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach(container => {
        ReactDOM.unmountComponentAtNode(container);
    });
}

const bootstrapComponents = () => {
    AOS.init({
         once: true
    });
    
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCartContainer />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearchContainer />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    var navbar = document.getElementById('navbar')
    if (navbar) {
        renderReact(
            <Provider store={store}>
                <NavigationContainer />
            </Provider>,
            navbar
        );
    }
    if (document.getElementById('facetedSearch')) {
        const FacetedSearchContainer = DynamicComponent({
            loader: () => import('./Containers/FacetedSearch.container')
        })
        renderReact(
            <Provider store={store}>
                <FacetedSearchContainer />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        const FacetedSearchCompactContainer = DynamicComponent({
            loader: () => import('./Containers/FacetedSearchCompact.container')
        })
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Containers/PersonList.container')
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Containers/AddressList.container')
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Containers/Checkout.container')
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider')
        });
        Array.from(document.querySelectorAll('.slider')).forEach((slider, index) => {
            const values = [...slider.querySelectorAll('.slider__block')].map(block => {
                return ({
                    image: block.dataset.image,
                    url: block.dataset.url,
                    text: block.dataset.text,
                })
            });
            if (values.length > 0) {
                renderReact(
                    <Slider values={values} />,
                    slider
                );
            }
        });
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Containers/BuyButton.Container')
        });
        Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
            const { articleNumber, quantityFieldId, href, cssClass, label } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <BuyButton {...{ label, articleNumber, quantityFieldId, href, cssClass }} />
                </Provider>,
                button
            );
        });
    }

    if (document.querySelectorAll('reorder-button').length > 0) {
        const ReorderButton = DynamicComponent({
            loader: () => import('./Containers/ReorderButton.Container')
        });
        Array.from(document.querySelectorAll('reorder-button')).forEach((button) => {
            const { cssClass, orderId, title } = button.dataset;
            const label = button.innerText;
            renderReact(
                <Provider store={store}>
                    <ReorderButton {...{ label, title, cssClass, orderId }} />
                </Provider>,
                button
            );
        });
    }

    var dynamicVideoElems = document.querySelectorAll('.react-dynamic-video')
    if (dynamicVideoElems && dynamicVideoElems.length > 0) {
        const DynamicVideoSource = DynamicComponent({
            loader: () => import('./Components/DynamicVideoSource')
        });
        Array.from(dynamicVideoElems).forEach(videoElem => {
            var videos = []
            
            Array.from(videoElem.querySelectorAll("source")).forEach(source => {
                videos.push({
                    src: source.src,
                    maxWidth: source.dataset["maxWidth"],
                    minWidth: source.dataset["minWidth"],
                    type: source.type
                })
            })

            renderReact(
                <Provider store={store}>
                    <DynamicVideoSource videoElem={videoElem} videos={videos} hideControls={videoElem.dataset["hideControls"] == "true"} />
                </Provider>,
                videoElem
            );
        });
    }

    var categoryListToggleElem = document.querySelector("#category-list-toggle")
    if(categoryListToggleElem) {
        const CategoryListToggle = DynamicComponent({
            loader: () => import('./Components/CategoryListToggle')
        });

        renderReact(
            <Provider store={store}>
                <CategoryListToggle />
            </Provider>,
            categoryListToggleElem
        );
    }

    var productSliders = document.querySelectorAll(".product-slider")
    if(productSliders && productSliders.length > 0) {
        const ProductSlider = DynamicComponent({
            loader: () => import('./Components/ProductSlider')
        })

        for(var productSliderElem of productSliders) {
            renderReact(
                <Provider store={store}>
                    <ProductSlider>{Array.from(productSliderElem.children)}</ProductSlider>
                </Provider>,
                productSliderElem
            )
        }
    }
    
    var searchWindow = document.getElementById('searchWindow')
    if (searchWindow) {
        renderReact(
            <Provider store={store}>
                <SearchWindow />
            </Provider>,
            searchWindow
        );
    }

    const productImageSlideshow = document.querySelector("#product-images")
    if(productImageSlideshow) {
        const ProductImageSlideshowContainer = DynamicComponent({
            loader: () => import("./Components/ProductImageSlideshow"),
        })

        const imgElements = productImageSlideshow.querySelectorAll("img")
        const images = []
        
        for(var img of imgElements) {
            images.push({ src: img.dataset["src"], alt: img.dataset["alt"], width: img.dataset["width"], height: img.dataset["height"], original: img.dataset["original"] })
        }

        renderReact(
            <Provider store={store}>
                <ProductImageSlideshowContainer images={images} />
            </Provider>,
            productImageSlideshow
        )
    }

    const productInfoNavElem = document.querySelector("#product-info-nav")
    if(productInfoNavElem) {
        const ProductInfoNavComponent = DynamicComponent({
            loader: () => import("./Components/ProductInfoNav"),
        })

        var sections = Array.from(document.querySelectorAll("#product-info-nav-container section"))

        renderReact(
            <Provider store={store}>
                <ProductInfoNavComponent sections={sections.map(e => ({ id: e.id, name: e.dataset["name"], element: e }))} />
            </Provider>,
            productInfoNavElem
        )
    }

    const variantPicker = document.getElementById("variant-picker")
    if(variantPicker) {
        const VariantPickerContainer = DynamicComponent({
            loader: () => import("./Containers/VariantPicker.container"),
        })

        renderReact(
            <Provider store={store}>
                <VariantPickerContainer />
            </Provider>,
            variantPicker
        )
    }

    var quickOrderContainer = document.getElementById("quick-order-container")
    if(quickOrderContainer) {
        const QuickOrderContainer = DynamicComponent({
            loader: () => import("./Containers/QuickOrder.container"),
        })
        renderReact(
            <Provider store={store}>
                <QuickOrderContainer />
            </Provider>,
            quickOrderContainer
        );
    }

    var toastContainer = document.getElementById("toast-container")
    if(toastContainer) {
        renderReact(
            <Provider store={store}>
                <ToastContainer position="top-right" closeOnClick={true} pauseOnHover={true} autoClose={5000} newestOnTop={true} />
            </Provider>,
            toastContainer
        )
    }

    var customerApplicationElem = document.getElementById('customer-application')
    if (customerApplicationElem) {
        const CustomerApplication = DynamicComponent({
            loader: () => import("./Components/CustomerApplication"),
        })
        renderReact(
            <Provider store={store}>
                <CustomerApplication integrityPolicyUrl={customerApplicationElem.dataset["integrityPolicyUrl"]} />
            </Provider>,
            customerApplicationElem
        );
    }
}
document.addEventListener('DOMContentLoaded', () => {
    const cookieConsent = document.getElementById('cookieConsent')
    if (cookieConsent) {
        renderReact(       
            <Provider store={store}>
                <CookieConsent {...JSON.parse(cookieConsent.dataset.modalCookie)}/>
            </Provider>,
            cookieConsent
        );
    }
});

bootstrapComponents();
